import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabaseClient'
import './KitsPersonalizados.css'

const KitsPersonalizados = ({ userRole, userEmpresa, onBack }) => {
  const [kits, setKits] = useState([])
  const [loading, setLoading] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [editingItem, setEditingItem] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortField, setSortField] = useState('kwh')
  const [sortDirection, setSortDirection] = useState('asc')

  const [formData, setFormData] = useState({
    kwh: '',
    valor_kit: '',
    porcentagem: '15',
    qtd_inversor: '1',
    qtd_modulos: '',
    marca_inversor: '',
    potencia_inversor: '',
    marca_modulo: '',
    potencia_modulo: ''
  })

  const [formErrors, setFormErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)

  const loadKits = useCallback(async () => {
    setLoading(true)
    try {
      let query = supabase
        .from('valor_kit_personalizado')
        .select('*')
        .order('kwh', { ascending: true })

      if (userEmpresa?.id) {
        query = query.or(`empresa_id.eq.${userEmpresa.id},empresa_id.is.null`)
      }

      const { data, error } = await query
      if (error) throw error
      setKits(data || [])
    } catch (error) {
      console.error('Erro ao carregar kits personalizados:', error)
      showNotification('Erro ao carregar: ' + error.message, 'error')
    } finally {
      setLoading(false)
    }
  }, [userEmpresa?.id])

  useEffect(() => {
    loadKits()
  }, [loadKits])

  const showNotification = (message, type = 'info') => {
    alert(`${type === 'error' ? 'Erro' : type === 'success' ? 'Sucesso' : 'Info'}: ${message}`)
  }

  const validateForm = () => {
    const errors = {}
    if (!formData.kwh || parseFloat(formData.kwh) <= 0) errors.kwh = 'Kwh deve ser maior que zero'
    if (!formData.valor_kit || parseFloat(formData.valor_kit) <= 0) errors.valor_kit = 'Valor do kit deve ser maior que zero'
    if (formData.porcentagem === '' || parseFloat(formData.porcentagem) < 0) errors.porcentagem = 'Porcentagem inválida'
    if (!formData.qtd_inversor || parseInt(formData.qtd_inversor) < 1) errors.qtd_inversor = 'Quantidade de inversor deve ser >= 1'
    if (!formData.qtd_modulos || parseInt(formData.qtd_modulos) < 1) errors.qtd_modulos = 'Quantidade de módulos deve ser >= 1'

    const existing = kits.find(
      (k) => Number(k.kwh) === Number(formData.kwh) && (!editingItem || k.id !== editingItem.id)
    )
    if (existing) errors.kwh = 'Já existe um kit com este Kwh'

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSave = async () => {
    if (!validateForm()) return

    setSubmitting(true)
    try {
      const kitData = {
        kwh: parseFloat(formData.kwh),
        valor_kit: parseFloat(formData.valor_kit),
        porcentagem: parseFloat(formData.porcentagem),
        qtd_inversor: parseInt(formData.qtd_inversor),
        qtd_modulos: parseInt(formData.qtd_modulos),
        empresa_id: userEmpresa?.id || null
      }

      if (editingItem) {
        const { error } = await supabase
          .from('valor_kit_personalizado')
          .update(kitData)
          .eq('id', editingItem.id)
        if (error) throw error

        // Atualizar ou inserir kits_valores
        const { data: existing } = await supabase
          .from('kits_valores_personalizado')
          .select('id')
          .eq('id_kit_personalizado', editingItem.id)
          .limit(1)

        const valorData = {
          id_kit_personalizado: editingItem.id,
          marca_inversor: formData.marca_inversor || null,
          potencia_inversor: formData.potencia_inversor ? parseFloat(formData.potencia_inversor) : null,
          marca_modulo: formData.marca_modulo || null,
          potencia_modulo: formData.potencia_modulo ? parseFloat(formData.potencia_modulo) : null
        }

        if (existing?.length) {
          await supabase.from('kits_valores_personalizado').update(valorData).eq('id', existing[0].id)
        } else {
          await supabase.from('kits_valores_personalizado').insert(valorData)
        }
        showNotification('Kit atualizado com sucesso!', 'success')
      } else {
        const { data: inserted, error } = await supabase.from('valor_kit_personalizado').insert(kitData).select('id').single()
        if (error) throw error

        const valorData = {
          id_kit_personalizado: inserted.id,
          marca_inversor: formData.marca_inversor || null,
          potencia_inversor: formData.potencia_inversor ? parseFloat(formData.potencia_inversor) : null,
          marca_modulo: formData.marca_modulo || null,
          potencia_modulo: formData.potencia_modulo ? parseFloat(formData.potencia_modulo) : null
        }
        await supabase.from('kits_valores_personalizado').insert(valorData)
        showNotification('Kit criado com sucesso!', 'success')
      }

      await loadKits()
      handleCloseForm()
    } catch (error) {
      console.error('Erro ao salvar:', error)
      showNotification('Erro ao salvar: ' + error.message, 'error')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (item) => {
    if (!window.confirm(`Excluir o kit ${item.kwh} Kwh? Os detalhes de inversor/módulo também serão removidos.`)) return

    try {
      const { error } = await supabase.from('valor_kit_personalizado').delete().eq('id', item.id)
      if (error) throw error
      showNotification('Kit excluído com sucesso!', 'success')
      await loadKits()
    } catch (error) {
      console.error('Erro ao excluir:', error)
      showNotification('Erro ao excluir: ' + error.message, 'error')
    }
  }

  const handleEdit = async (item) => {
    setEditingItem(item)
    setFormData({
      kwh: item.kwh?.toString() || '',
      valor_kit: item.valor_kit?.toString() || '',
      porcentagem: item.porcentagem?.toString() || '15',
      qtd_inversor: item.qtd_inversor?.toString() || '1',
      qtd_modulos: item.qtd_modulos?.toString() || '',
      marca_inversor: '',
      potencia_inversor: '',
      marca_modulo: '',
      potencia_modulo: ''
    })

    const { data } = await supabase
      .from('kits_valores_personalizado')
      .select('marca_inversor, potencia_inversor, marca_modulo, potencia_modulo')
      .eq('id_kit_personalizado', item.id)
      .limit(1)

    const kv = data?.[0]
    if (kv) {
      setFormData((prev) => ({
        ...prev,
        marca_inversor: kv.marca_inversor || '',
        potencia_inversor: kv.potencia_inversor?.toString() || '',
        marca_modulo: kv.marca_modulo || '',
        potencia_modulo: kv.potencia_modulo?.toString() || ''
      }))
    }
    setShowForm(true)
  }

  const handleCreate = () => {
    setEditingItem(null)
    setFormData({
      kwh: '',
      valor_kit: '',
      porcentagem: '15',
      qtd_inversor: '1',
      qtd_modulos: '',
      marca_inversor: '',
      potencia_inversor: '',
      marca_modulo: '',
      potencia_modulo: ''
    })
    setFormErrors({})
    setShowForm(true)
  }

  const handleCloseForm = () => {
    setShowForm(false)
    setEditingItem(null)
    setFormErrors({})
  }

  const formatCurrency = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)

  const filtered = kits.filter(
    (k) =>
      String(k.kwh).includes(searchTerm) ||
      formatCurrency(k.valor_kit).toLowerCase().includes(searchTerm.toLowerCase())
  )
  const sorted = [...filtered].sort((a, b) => {
    const av = a[sortField]
    const bv = b[sortField]
    return sortDirection === 'asc' ? (av > bv ? 1 : -1) : av < bv ? 1 : -1
  })

  const handleSort = (field) => {
    if (sortField === field) setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))
    else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const totalPreview =
    formData.valor_kit && formData.porcentagem
      ? parseFloat(formData.valor_kit) + (parseFloat(formData.valor_kit) * parseFloat(formData.porcentagem || 0)) / 100
      : null

  if (loading) {
    return (
      <div className="kits-personalizados-container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Carregando kits...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="kits-personalizados-container">
      <div className="kits-personalizados-header">
        <div className="header-content">
          <h1>🔧 Kits Personalizados</h1>
          <p>Cadastre kits de valores para propostas personalizadas na Calculadora Solar</p>
          {onBack && (
            <button onClick={onBack} className="back-button">
              ← Voltar
            </button>
          )}
        </div>
      </div>

      <div className="kits-personalizados-controls">
        <div className="controls-row">
          <div className="search-container">
            <input
              type="text"
              placeholder="🔍 Buscar por Kwh ou valor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <button onClick={handleCreate} className="btn btn-primary">
            ➕ Novo Kit
          </button>
        </div>
      </div>

      <div className="kits-personalizados-table-container">
        {sorted.length === 0 ? (
          <div className="no-data">
            <div className="no-data-icon">📦</div>
            <h3>Nenhum kit encontrado</h3>
            <p>
              {searchTerm
                ? 'Nenhum kit corresponde à busca.'
                : 'Não há kits cadastrados. Clique em "Novo Kit" para começar.'}
            </p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="kits-personalizados-table">
              <thead>
                <tr>
                  <th className={`sortable ${sortField === 'kwh' ? sortDirection : ''}`} onClick={() => handleSort('kwh')}>
                    Kwh {sortField === 'kwh' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className={`sortable ${sortField === 'valor_kit' ? sortDirection : ''}`} onClick={() => handleSort('valor_kit')}>
                    Valor Kit
                  </th>
                  <th>Porcentagem</th>
                  <th>Total</th>
                  <th>Qtd Inversor</th>
                  <th>Qtd Módulos</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((item) => (
                  <tr key={item.id}>
                    <td className="kwh-cell">
                      <span className="kwh-badge">{item.kwh}</span>
                    </td>
                    <td>{formatCurrency(item.valor_kit)}</td>
                    <td className="porcentagem-cell">{item.porcentagem}%</td>
                    <td className="total-cell">{formatCurrency(item.total)}</td>
                    <td>{item.qtd_inversor}</td>
                    <td>{item.qtd_modulos}</td>
                    <td className="actions-cell">
                      <div className="action-buttons">
                        <button onClick={() => handleEdit(item)} className="btn btn-sm btn-outline-primary" title="Editar">
                          ✏️
                        </button>
                        <button onClick={() => handleDelete(item)} className="btn btn-sm btn-outline-danger" title="Excluir">
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content modal-kit-large">
            <div className="modal-header">
              <h2>{editingItem ? '✏️ Editar Kit' : '➕ Novo Kit'}</h2>
              <button onClick={handleCloseForm} className="close-button">
                ✕
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
                <div className="form-section">
                  <h4>📊 Dados do Kit</h4>
                  <div className="form-row-grid">
                    <div className="form-group">
                      <label htmlFor="kwh">Consumo (Kwh):</label>
                      <input
                        type="number"
                        id="kwh"
                        value={formData.kwh}
                        onChange={(e) => setFormData((p) => ({ ...p, kwh: e.target.value }))}
                        className={`form-input ${formErrors.kwh ? 'error' : ''}`}
                        placeholder="Ex: 300"
                        min="0.01"
                        step="0.01"
                      />
                      {formErrors.kwh && <span className="error-message">{formErrors.kwh}</span>}
                    </div>
                    <div className="form-group">
                      <label htmlFor="valor_kit">Valor Kit (R$):</label>
                      <input
                        type="number"
                        id="valor_kit"
                        value={formData.valor_kit}
                        onChange={(e) => setFormData((p) => ({ ...p, valor_kit: e.target.value }))}
                        className={`form-input ${formErrors.valor_kit ? 'error' : ''}`}
                        placeholder="Ex: 6500"
                        min="0"
                        step="0.01"
                      />
                      {formErrors.valor_kit && <span className="error-message">{formErrors.valor_kit}</span>}
                    </div>
                    <div className="form-group">
                      <label htmlFor="porcentagem">Porcentagem (%):</label>
                      <input
                        type="number"
                        id="porcentagem"
                        value={formData.porcentagem}
                        onChange={(e) => setFormData((p) => ({ ...p, porcentagem: e.target.value }))}
                        className={`form-input ${formErrors.porcentagem ? 'error' : ''}`}
                        placeholder="Ex: 15"
                        min="0"
                        step="0.01"
                      />
                      {formErrors.porcentagem && <span className="error-message">{formErrors.porcentagem}</span>}
                    </div>
                    <div className="form-group">
                      <label htmlFor="qtd_inversor">Qtd. Inversor:</label>
                      <input
                        type="number"
                        id="qtd_inversor"
                        value={formData.qtd_inversor}
                        onChange={(e) => setFormData((p) => ({ ...p, qtd_inversor: e.target.value }))}
                        className={`form-input ${formErrors.qtd_inversor ? 'error' : ''}`}
                        min="1"
                        step="1"
                      />
                      {formErrors.qtd_inversor && <span className="error-message">{formErrors.qtd_inversor}</span>}
                    </div>
                    <div className="form-group">
                      <label htmlFor="qtd_modulos">Qtd. Módulos:</label>
                      <input
                        type="number"
                        id="qtd_modulos"
                        value={formData.qtd_modulos}
                        onChange={(e) => setFormData((p) => ({ ...p, qtd_modulos: e.target.value }))}
                        className={`form-input ${formErrors.qtd_modulos ? 'error' : ''}`}
                        placeholder="Ex: 10"
                        min="1"
                        step="1"
                      />
                      {formErrors.qtd_modulos && <span className="error-message">{formErrors.qtd_modulos}</span>}
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h4>⚡ Inversor e Módulo</h4>
                  <div className="form-row-grid">
                    <div className="form-group">
                      <label htmlFor="marca_inversor">Marca Inversor:</label>
                      <input
                        type="text"
                        id="marca_inversor"
                        value={formData.marca_inversor}
                        onChange={(e) => setFormData((p) => ({ ...p, marca_inversor: e.target.value }))}
                        className="form-input"
                        placeholder="Ex: Growatt"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="potencia_inversor">Potência Inversor (W):</label>
                      <input
                        type="number"
                        id="potencia_inversor"
                        value={formData.potencia_inversor}
                        onChange={(e) => setFormData((p) => ({ ...p, potencia_inversor: e.target.value }))}
                        className="form-input"
                        placeholder="Ex: 6000"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="marca_modulo">Marca Módulo:</label>
                      <input
                        type="text"
                        id="marca_modulo"
                        value={formData.marca_modulo}
                        onChange={(e) => setFormData((p) => ({ ...p, marca_modulo: e.target.value }))}
                        className="form-input"
                        placeholder="Ex: Canadian Solar"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="potencia_modulo">Potência Módulo (W):</label>
                      <input
                        type="number"
                        id="potencia_modulo"
                        value={formData.potencia_modulo}
                        onChange={(e) => setFormData((p) => ({ ...p, potencia_modulo: e.target.value }))}
                        className="form-input"
                        placeholder="Ex: 450"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>

                {totalPreview != null && (
                  <div className="preview-total">
                    <h4>📊 Prévia do Total:</h4>
                    <p>
                      <strong>{formatCurrency(totalPreview)}</strong>
                    </p>
                  </div>
                )}
              </form>
            </div>
            <div className="modal-footer">
              <button onClick={handleCloseForm} className="btn btn-secondary" disabled={submitting}>
                Cancelar
              </button>
              <button onClick={handleSave} className="btn btn-primary" disabled={submitting}>
                {submitting ? '⏳ Salvando...' : editingItem ? '💾 Atualizar' : '➕ Criar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default KitsPersonalizados
